<template>
  <v-container>
    <loading-spinner v-if="loading"></loading-spinner>
    <template v-else>
      <builder-app-bar
        :saving="saving"
        :saved="saved"
        v-on:complete="save(true)"
        v-on:save="save"
      ></builder-app-bar>
      <builder-left-nav
        v-on:moveup="moveQuestionUp"
        v-on:movedown="moveQuestionDown"
        v-on:delete="deleteQuestion"
        v-on:duplicate="duplicateQuestion"
        v-on:addquestion="addQuestion"
      ></builder-left-nav>
      <v-form ref="form" v-model="valid">
        <v-row>
          <v-col cols="12">
            <survey-overview-form></survey-overview-form>
          </v-col>
        </v-row>
        <v-row v-if="survey_questions.length == 0">
          <v-col cols="12" class="text-right">
            <v-menu absolute>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="accent" v-bind="attrs" v-on="on">
                  <v-icon>add_circle</v-icon>Add first question
                </v-btn>
              </template>
              <survey-question-type-list
                v-on:select="addQuestion($event)"
              ></survey-question-type-list>
            </v-menu>
          </v-col>
        </v-row>
        <v-row v-for="(question, i) in survey_questions" :key="i">
          <v-col cols="12">
            <survey-builder-card
              v-on:moveup="moveQuestionUp(i)"
              v-on:movedown="moveQuestionDown(i)"
              v-on:delete="deleteQuestion(i)"
              v-on:duplicate="duplicateQuestion(i)"
              :question="question"
              :index="i"
              :totalQuestionCount="survey_questions.length"
            >
            </survey-builder-card>
          </v-col>
          <v-col cols="12" class="text-right">
            <v-menu absolute>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="accent" v-bind="attrs" v-on="on">
                  <v-icon>add_circle</v-icon>Add next question
                </v-btn>
              </template>
              <survey-question-type-list
                v-on:select="addQuestion($event, i + 1)"
              ></survey-question-type-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <survey-schedule-dialog
      ref="scheduleDialog"
      v-on:save="returnToList"
      show-skip
      v-on:skip="returnToList"
    ></survey-schedule-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import BuilderLeftNavigation from "@/components/layout/BuilderLeftNavigation";
import BuilderAppBar from "@/components/layout/BuilderAppBar";
import SurveyBuilderCard from "@/components/surveys/SurveyBuilderCard";
import SurveyQuestionTypeList from "@/components/surveys/SurveyQuestionTypeList";
import SurveyOverviewForm from "@/components/surveys/SurveyOverviewForm";
import SurveyScheduleDialog from "@/components/surveys/SurveyScheduleDialog";
export default {
  name: "SurveyBuilder",
  components: {
    "survey-overview-form": SurveyOverviewForm,
    "builder-left-nav": BuilderLeftNavigation,
    "builder-app-bar": BuilderAppBar,
    "survey-builder-card": SurveyBuilderCard,
    "survey-question-type-list": SurveyQuestionTypeList,
    SurveyScheduleDialog
  },
  data: () => ({
    valid: true,
    loading: false,
    changes: 0,
    saved: true,
    saving: false
  }),
  watch: {
    survey: {
      handler() {
        if (this.changes > 1) {
          this.saved = false;
        }
        this.changes++;
      },
      deep: true
    },
    survey_questions: {
      handler() {
        if (this.changes > 1) {
          this.saved = false;
        }
        this.changes++;
      },
      deep: true
    }
  },
  computed: {
    isAdd() {
      return this.$route.params.id == "add";
    },
    ...mapGetters({
      survey: "survey/edit_survey",
      survey_questions: "survey/survey_questions"
    })
  },
  created() {
    this.loading = true;
    this.$store.dispatch("company/getCoreValues");
    let surveyId = this.isAdd ? "" : this.$route.params.id;
    if (surveyId) {
      this.$store
        .dispatch("survey/getSurvey", surveyId)
        .then(() => {
          this.$store
            .dispatch("survey/getSurveyQuestions")
            .catch(error => {
              this.logError(error);
              this.$root.$alert(
                "Error Occurred",
                "An error occured while loading this survey's questions."
              );
            })
            .finally(() => {
              this.loading = false;
            });
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading this survey."
          );
        });
    } else {
      this.$store.dispatch("survey/resetSurvey").then(() => {
        let templateId = this.$route.query.templateId;
        if (templateId) {
          this.$store
            .dispatch("survey/getSurveyTemplate", templateId)
            .then(() => {})
            .catch(error => {
              this.logError(error);
              this.$root.$alert(
                "Error Occurred",
                "An error occured while loading survey template questions."
              );
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          this.loading = false;
        }
      });
    }
  },
  mounted() {
    this.saved = true;
  },
  methods: {
    returnToList() {
      this.$router.push({ path: "/features/surveys" });
    },
    save(finish) {
      if (this.$refs.form.validate()) {
        this.saving = true;
        let survey = {
          survey: this._.clone(this.survey),
          questions: this._.clone(this.survey_questions)
        };
        this.$store
          .dispatch("survey/saveSurveyWithQuestions", survey)
          .then(id => {
            if (finish) {
              if (this.isAdd) {
                this.$refs.scheduleDialog.showDialog(id, null);
              } else {
                this.returnToList();
              }
            }

            this.saved = true;
            this.changes = 0;
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while saving a survey."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    async deleteQuestion(index) {
      if (
        await this.$root.$confirm(
          "Delete question",
          "Are you sure you want to delete this question?",
          { color: "red" }
        )
      ) {
        if (index >= 0) {
          this.survey_questions.splice(index, 1);
          this.saved = false;
        }
      }
    },
    duplicateQuestion(index) {
      if (index >= 0) {
        let copy = this._.cloneDeep(this.survey_questions[index]);
        copy.question = "Copy - " + copy.question;
        copy.id = "";
        this._.each(copy.multipleChoiceAnswers, function(item) {
          item.id = "";
        });
        this.survey_questions.push(copy);
        this.saved = false;
        this.goToBuilderCard(this.survey_questions.length - 1);
      }
    },
    moveQuestionUp(index) {
      if (index >= 0) {
        this.survey_questions[index - 1] = this.survey_questions.splice(
          index,
          1,
          this.survey_questions[index - 1]
        )[0];
        this.saved = false;
      }
      this.goToBuilderCard(index - 1);
    },
    moveQuestionDown(index) {
      if (index >= 0) {
        this.survey_questions[index] = this.survey_questions.splice(
          index + 1,
          1,
          this.survey_questions[index]
        )[0];
        this.saved = false;
      }
      this.goToBuilderCard(index + 1);
    },
    addQuestion(questionTypeId, index) {
      let newQuestion = { questionTypeId, isRequired: true };
      if (questionTypeId == this.$enums.questionTypes.MULTIPLE_CHOICE) {
        newQuestion.multipleChoiceAnswers = [{}, {}];
      }
      if (index != undefined) {
        this.survey_questions.splice(index, 0, newQuestion);
      } else {
        this.survey_questions.push(newQuestion);
        index = this.survey_questions.length - 1;
      }
      this.saved = false;
      this.goToBuilderCard(index);
    },
    goToBuilderCard(index) {
      setTimeout(() => {
        this.$vuetify.goTo(".builder-card-" + index);
      }, 400);
    }
  }
};
</script>
